<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TramiteStageTimeline from './TramiteStageTimeline.vue';
import DocumentRequestsPanel from 'dashboard/components/documents/DocumentRequestsPanel.vue';
import RequirementsPanel from 'dashboard/components/documents/RequirementsPanel.vue';
import DocumentFileList from 'dashboard/components/documents/DocumentFileList.vue';
import TramiteFormsPanel from 'dashboard/components/documents/TramiteFormsPanel.vue';
import tramiteDocumentRequestsApi from 'dashboard/api/tramiteDocumentRequests';
import tramiteRequirementsApi from 'dashboard/api/tramiteRequirements';
import tramiteDocumentsApi from 'dashboard/api/tramiteDocuments';
import TramitesAPI from 'dashboard/api/tramites';

const props = defineProps({
  tramiteId: {
    type: [String, Number],
    default: 0,
  },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const resolvedId = computed(() => props.tramiteId || route.params.tramiteId);

const isLoading = ref(true);
const activeTab = ref('overview');

// Assignee dropdown
const assigneeOpen = ref(false);
const assigneeSearch = ref('');
const assigneeRef = ref(null);
const assigneeSearchInput = ref(null);

const isAdvancing = ref(false);
const isRecordingPayment = ref(false);

// Activity / comments
const timelineRows = ref([]);
const newComment = ref('');
const isPrivate = ref(false);
const clientVisible = ref(false);
const isSendingComment = ref(false);

const agents = useMapGetter('agents/getAgents');

const tramite = computed(() =>
  store.getters['tramites/getTramiteById'](resolvedId.value)
);

// Public document-request share token issuer for this tramite.
const documentShareFn = id => TramitesAPI.createDocumentRequestShare(id);

const STATUS_PILL = {
  draft: 'bg-n-slate-9 text-white',
  submitted: 'bg-n-blue-9 text-white',
  in_review: 'bg-n-amber-9 text-white',
  awaiting_citizen: 'bg-n-amber-9 text-white',
  approved: 'bg-n-teal-9 text-white',
  rejected: 'bg-n-ruby-9 text-white',
  completed: 'bg-n-teal-9 text-white',
  cancelled: 'bg-n-slate-8 text-white',
};
const PRIORITY_FLAG = {
  urgent: 'text-n-ruby-9',
  high: 'text-n-amber-9',
  medium: 'text-n-blue-9',
  low: 'text-n-slate-9',
};

const statusPill = computed(
  () => STATUS_PILL[tramite.value?.status] || STATUS_PILL.draft
);
const statusLabel = computed(() =>
  tramite.value
    ? t(`TRAMITES.STATUS.${(tramite.value.status || 'draft').toUpperCase()}`)
    : ''
);
const priorityLabel = computed(() =>
  tramite.value?.priority
    ? t(`TRAMITES.PRIORITY.${tramite.value.priority.toUpperCase()}`)
    : ''
);
const folio = computed(
  () =>
    tramite.value?.folio ||
    `${t('TRAMITES.NUMBER_PREFIX')}${tramite.value?.display_id}`
);

const customAttributes = computed(
  () => tramite.value?.custom_attributes || {}
);
const hasCustomAttributes = computed(
  () => Object.keys(customAttributes.value).length > 0
);

const filteredAgents = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  const all = [
    { id: null, name: t('TRAMITES.DETAIL.NO_ASSIGNEE') },
    ...(agents.value || []),
  ];
  if (!q) return all;
  return all.filter(a => a.name.toLowerCase().includes(q));
});

const currentAssigneeName = computed(() => {
  if (!tramite.value?.assignee_id) return t('TRAMITES.DETAIL.NO_ASSIGNEE');
  return (
    agents.value?.find(a => a.id === tramite.value.assignee_id)?.name ||
    tramite.value?.assignee?.name ||
    t('TRAMITES.DETAIL.NO_ASSIGNEE')
  );
});

const paymentAmountLabel = computed(() => {
  const amount = tramite.value?.payment_amount;
  if (amount === null || amount === undefined || amount === '') return '';
  return `${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${tramite.value?.payment_currency || ''}`.trim();
});

const fetchTramite = async () => {
  isLoading.value = true;
  try {
    return await store.dispatch('tramites/fetchTramite', {
      id: resolvedId.value,
    });
  } finally {
    isLoading.value = false;
  }
};

const fetchTimeline = async () => {
  try {
    const rows = await store.dispatch('tramites/fetchTimeline', {
      id: resolvedId.value,
    });
    timelineRows.value = rows || [];
  } catch {
    timelineRows.value = [];
  }
};

const goBack = () => router.push({ name: 'tramites_list' });

const selectAssignee = async agentId => {
  assigneeOpen.value = false;
  assigneeSearch.value = '';
  try {
    await store.dispatch('tramites/assignTramite', {
      id: tramite.value.id,
      assigneeId: agentId,
    });
    await fetchTramite();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TRAMITES.STAGES.ADVANCE_ERROR'));
  }
};

const onAdvanceStage = async ({ actionName, comment }) => {
  isAdvancing.value = true;
  try {
    await store.dispatch('tramites/advanceStage', {
      id: tramite.value.id,
      actionName,
      comment,
    });
    await fetchTramite();
    await fetchTimeline();
    useAlert(t('TRAMITES.STAGES.ADVANCE_SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TRAMITES.STAGES.ADVANCE_ERROR'));
  } finally {
    isAdvancing.value = false;
  }
};

const onRecordPayment = async status => {
  isRecordingPayment.value = true;
  try {
    await store.dispatch('tramites/recordPayment', {
      id: tramite.value.id,
      status,
    });
    await fetchTramite();
    await fetchTimeline();
    useAlert(t('TRAMITES.PAYMENT.RECORD_SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TRAMITES.PAYMENT.RECORD_ERROR'));
  } finally {
    isRecordingPayment.value = false;
  }
};

const addComment = async () => {
  if (!newComment.value.trim()) return;
  isSendingComment.value = true;
  try {
    await store.dispatch('tramites/createComment', {
      id: tramite.value.id,
      message: newComment.value.trim(),
      isPrivate: isPrivate.value,
      clientVisible: isPrivate.value && clientVisible.value,
    });
    newComment.value = '';
    isPrivate.value = false;
    clientVisible.value = false;
    await fetchTimeline();
    useAlert(t('TRAMITES.COMMENTS.SEND_SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TRAMITES.COMMENTS.SEND_ERROR'));
  } finally {
    isSendingComment.value = false;
  }
};

const closeDropdowns = e => {
  if (assigneeRef.value && !assigneeRef.value.contains(e.target)) {
    assigneeOpen.value = false;
    assigneeSearch.value = '';
  }
};

const formatDateTime = date =>
  date
    ? new Date(date).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

const isActivityRow = row => row?.metadata?.message_type === 'activity';

watch(assigneeOpen, async val => {
  if (val) {
    await nextTick();
    assigneeSearchInput.value?.focus();
  }
});

watch(resolvedId, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    await fetchTramite();
    await fetchTimeline();
  }
});

onMounted(async () => {
  document.addEventListener('click', closeDropdowns);
  await fetchTramite();
  await fetchTimeline();
  store.dispatch('agents/get');
});
</script>

<template>
  <div v-if="tramite" class="flex flex-col h-full bg-n-background lg:flex-row">
    <!-- ============ LEFT: main content ============ -->
    <main class="flex flex-col flex-1 min-w-0 overflow-y-auto">
      <!-- Breadcrumb -->
      <div class="flex items-center justify-between gap-2 px-6 pt-4 pb-1">
        <div class="flex items-center gap-2 min-w-0 text-sm text-n-slate-11">
          <Button
            icon="i-lucide-arrow-left"
            variant="ghost"
            color="slate"
            size="sm"
            @click="goBack"
          />
          <span class="flex items-center gap-1.5">
            <span class="i-lucide-stamp text-n-slate-10" />
            {{ t('TRAMITES.DETAIL.BACK') }}
          </span>
          <span class="text-n-slate-8">/</span>
          <span class="font-mono text-n-slate-10">{{ folio }}</span>
        </div>
      </div>

      <div class="flex flex-col w-full max-w-3xl gap-6 px-6 pt-2 pb-10">
        <!-- Title + status + priority -->
        <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 class="flex-1 min-w-[12rem] text-2xl font-bold leading-snug text-n-slate-12">
            {{ tramite.title }}
          </h1>
          <span
            class="inline-flex items-center px-2.5 h-7 text-sm font-medium rounded-md"
            :class="statusPill"
          >
            {{ statusLabel }}
          </span>
          <span class="inline-flex items-center gap-1.5 text-sm text-n-slate-11">
            <span class="i-lucide-flag" :class="PRIORITY_FLAG[tramite.priority]" />
            {{ priorityLabel }}
          </span>
        </div>

        <!-- Metadata grid -->
        <section class="grid grid-cols-1 gap-x-10 gap-y-3.5 sm:grid-cols-2">
          <!-- Type -->
          <div class="flex items-start gap-3 min-w-0">
            <span class="flex items-center gap-2 text-sm w-28 shrink-0 text-n-slate-11">
              <span class="i-lucide-stamp text-n-slate-10" />
              {{ t('TRAMITES.FIELDS.TYPE') }}
            </span>
            <span class="text-sm text-n-slate-12 truncate">
              {{ tramite.tramite_type?.name || '—' }}
            </span>
          </div>

          <!-- Assignee -->
          <div class="flex items-start gap-3 min-w-0">
            <span class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11">
              <span class="i-lucide-user text-n-slate-10" />
              {{ t('TRAMITES.FIELDS.ASSIGNEE') }}
            </span>
            <div ref="assigneeRef" class="relative flex-1 min-w-0">
              <button
                v-if="!assigneeOpen"
                type="button"
                class="flex items-center justify-between w-full gap-2 px-2 text-sm text-left rounded-md h-7 hover:bg-n-alpha-1"
                :class="tramite.assignee_id ? 'text-n-slate-12' : 'text-n-slate-10'"
                @click.stop="assigneeOpen = true"
              >
                <span class="flex items-center gap-2 truncate">
                  <Avatar
                    v-if="tramite.assignee_id"
                    :name="currentAssigneeName"
                    :size="20"
                  />
                  <span class="truncate">{{ currentAssigneeName }}</span>
                </span>
                <span class="i-lucide-chevron-down text-n-slate-10 shrink-0" />
              </button>
              <div
                v-else
                class="flex items-center px-2 rounded-md h-7 outline-1 outline outline-n-brand bg-n-alpha-black2"
              >
                <input
                  ref="assigneeSearchInput"
                  v-model="assigneeSearch"
                  type="text"
                  class="w-full text-sm bg-transparent border-none reset-base text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                  @click.stop
                />
              </div>
              <ul
                v-if="assigneeOpen"
                class="absolute left-0 right-0 z-50 py-1 mt-1 overflow-y-auto border rounded-lg shadow-lg bg-n-solid-2 border-n-weak max-h-44"
              >
                <li
                  v-for="agent in filteredAgents"
                  :key="agent.id ?? 'none'"
                  class="flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-n-alpha-1"
                  :class="
                    (agent.id ?? null) === (tramite.assignee_id ?? null)
                      ? 'text-n-blue-text font-medium'
                      : 'text-n-slate-12'
                  "
                  @mousedown.prevent="selectAssignee(agent.id)"
                >
                  <Avatar v-if="agent.id" :name="agent.name" :size="20" />
                  <span class="truncate">{{ agent.name }}</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Contact -->
          <div class="flex items-start gap-3 min-w-0">
            <span class="flex items-center gap-2 text-sm w-28 shrink-0 text-n-slate-11">
              <span class="i-lucide-contact text-n-slate-10" />
              {{ t('TRAMITES.FIELDS.CONTACT') }}
            </span>
            <span class="text-sm text-n-slate-12 truncate">
              {{ tramite.contact?.name || t('TRAMITES.FIELDS.NO_CONTACT') }}
            </span>
          </div>

          <!-- Empresa -->
          <div v-if="tramite.empresa" class="flex items-start gap-3 min-w-0">
            <span class="flex items-center gap-2 text-sm w-28 shrink-0 text-n-slate-11">
              <span class="i-lucide-building-2 text-n-slate-10" />
              {{ t('TRAMITES.FIELDS.EMPRESA') }}
            </span>
            <span class="text-sm text-n-slate-12 truncate">
              {{ tramite.empresa.name }}
            </span>
          </div>
        </section>

        <!-- Description -->
        <p v-if="tramite.description" class="text-sm whitespace-pre-line text-n-slate-11">
          {{ tramite.description }}
        </p>

        <!-- Stage timeline -->
        <TramiteStageTimeline
          :tramite="tramite"
          :is-advancing="isAdvancing"
          @advance="onAdvanceStage"
        />

        <!-- Payment -->
        <section
          v-if="tramite.payment_required"
          class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak"
        >
          <div class="flex flex-wrap gap-3 justify-between items-center">
            <div class="flex gap-2 items-center">
              <span class="i-lucide-banknote text-n-slate-10" />
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ t('TRAMITES.PAYMENT.TITLE') }}
              </h3>
              <span class="text-sm font-medium text-n-slate-12">
                {{ paymentAmountLabel }}
              </span>
              <span
                class="inline-flex items-center px-2 h-6 text-xs font-medium rounded-md"
                :class="
                  tramite.payment_settled
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-amber-3 text-n-amber-11'
                "
              >
                {{ t(`TRAMITES.PAYMENT.STATUS.${tramite.payment_status}`) }}
              </span>
            </div>
            <div v-if="!tramite.payment_settled" class="flex gap-2">
              <Button
                size="sm"
                :is-loading="isRecordingPayment"
                :label="t('TRAMITES.PAYMENT.MARK_PAID')"
                @click="onRecordPayment('paid')"
              />
              <Button
                size="sm"
                variant="faded"
                color="slate"
                :disabled="isRecordingPayment"
                :label="t('TRAMITES.PAYMENT.WAIVE')"
                @click="onRecordPayment('waived')"
              />
            </div>
          </div>
          <p v-if="tramite.paid_at" class="text-xs text-n-slate-10">
            {{ t('TRAMITES.PAYMENT.PAID_AT') }} {{ formatDateTime(tramite.paid_at) }}
          </p>
        </section>

        <!-- Document requests -->
        <section class="flex flex-col gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('TRAMITES.DETAIL.SECTIONS.DOCUMENT_REQUESTS') }}
          </h3>
          <DocumentRequestsPanel
            :api="tramiteDocumentRequestsApi"
            :owner-id="tramite.id"
            :share-fn="documentShareFn"
          />
        </section>

        <!-- Requirements (requisitos) — advisory eligibility + attestation checklist -->
        <section class="flex flex-col gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('TRAMITES.DETAIL.SECTIONS.REQUIREMENTS') }}
          </h3>
          <RequirementsPanel
            :api="tramiteRequirementsApi"
            :owner-id="tramite.id"
          />
        </section>

        <!-- Formularios (form responses) -->
        <section class="flex flex-col gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('TRAMITES.FORMS.TITLE') }}
          </h3>
          <TramiteFormsPanel
            :tramite-id="tramite.id"
            :tramite-type-id="tramite.tramite_type_id"
          />
        </section>

        <!-- File documents -->
        <section class="flex flex-col gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('TRAMITES.DETAIL.SECTIONS.DOCUMENTS') }}
          </h3>
          <DocumentFileList
            :api="tramiteDocumentsApi"
            :owner-id="tramite.id"
          />
        </section>

        <!-- Custom attributes (read-only display) -->
        <section v-if="hasCustomAttributes" class="flex flex-col gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('TRAMITES.DETAIL.SECTIONS.DETAILS') }}
          </h3>
          <dl class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div
              v-for="(value, key) in customAttributes"
              :key="key"
              class="flex flex-col gap-0.5"
            >
              <dt class="text-xs text-n-slate-10">{{ key }}</dt>
              <dd class="text-sm text-n-slate-12">{{ value }}</dd>
            </div>
          </dl>
        </section>
      </div>
    </main>

    <!-- ============ RIGHT: activity ============ -->
    <aside
      class="flex flex-col w-full lg:w-96 shrink-0 border-t lg:border-t-0 lg:border-l border-n-weak"
    >
      <header class="px-4 py-3 border-b border-n-weak shrink-0">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('TRAMITES.DETAIL.SECTIONS.ACTIVITY') }}
        </h3>
      </header>

      <div class="flex flex-col flex-1 gap-3 px-4 py-3 overflow-y-auto">
        <p
          v-if="!timelineRows.length"
          class="text-sm italic text-n-slate-10"
        >
          {{ t('TRAMITES.COMMENTS.EMPTY') }}
        </p>
        <div
          v-for="(row, idx) in timelineRows"
          :key="row.id || idx"
          class="flex flex-col gap-1"
        >
          <div
            v-if="isActivityRow(row)"
            class="text-xs text-n-slate-10"
          >
            {{ row.message }}
            <span class="ml-1 text-n-slate-9">{{ formatDateTime(row.created_at) }}</span>
          </div>
          <div
            v-else
            class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1"
          >
            <div class="flex items-center gap-2 text-xs text-n-slate-10">
              <span class="font-medium text-n-slate-11">
                {{ row.user?.name || row.sender?.name || '—' }}
              </span>
              <span>{{ formatDateTime(row.created_at) }}</span>
              <span v-if="row.private" class="i-lucide-lock text-n-amber-10" />
            </div>
            <p class="text-sm whitespace-pre-line text-n-slate-12">
              {{ row.message }}
            </p>
          </div>
        </div>
      </div>

      <!-- Composer -->
      <footer class="flex flex-col gap-2 px-4 py-3 border-t border-n-weak shrink-0">
        <textarea
          v-model="newComment"
          rows="2"
          :placeholder="t('TRAMITES.COMMENTS.PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
        />
        <div class="flex flex-wrap items-center gap-3">
          <label class="flex items-center gap-1.5 text-xs text-n-slate-11">
            <input v-model="isPrivate" type="checkbox" class="reset-base" />
            {{ t('TRAMITES.COMMENTS.PRIVATE') }}
          </label>
          <label
            v-if="isPrivate"
            class="flex items-center gap-1.5 text-xs text-n-slate-11"
          >
            <input v-model="clientVisible" type="checkbox" class="reset-base" />
            {{ t('TRAMITES.COMMENTS.CLIENT_VISIBLE') }}
          </label>
          <Button
            :label="t('TRAMITES.COMMENTS.SEND')"
            size="sm"
            color="blue"
            class="ml-auto"
            :is-loading="isSendingComment"
            :disabled="!newComment.trim() || isSendingComment"
            @click="addComment"
          />
        </div>
      </footer>
    </aside>
  </div>

  <!-- Loading / not found -->
  <div v-else class="flex items-center justify-center h-full">
    <Spinner v-if="isLoading" />
    <p v-else class="text-sm italic text-n-slate-10">
      {{ t('TRAMITES.DETAIL.NOT_FOUND') }}
    </p>
  </div>
</template>
